import { useParams, Link } from 'react-router-dom';
import { Container, Section } from '@vnetwork/ui/layout';
import {
  Badge,
  Button,
  CardBody,
} from '@vnetwork/ui/primitives';
import { StatusPill, Timeline, type TimelineItem } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Skeleton, Card } from '@vnetwork/ui/primitives';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { TableLink } from '@/components/table-link';
import { DetailHeader } from '@/components/detail-header';
import { Pencil } from 'lucide-react';
import { formatDateTime } from '@/lib/format';
import { useTicket, type Ticket } from '@/api/tickets';
import {
  TICKET_STATUS_COMPLETED,
  TICKET_STATUS_CLOSED,
} from '@/api/tickets-constants';
import { ticketStatusTone, ticketStatusLabel, ticketPriorityTone } from './ticket-status-tones';
import { WorkflowTab } from './workflow-tab';
import { ConversationTab } from './conversation-tab';
import { AttachmentsTab } from './attachments-tab';
import { FeedbackTab } from './feedback-tab';
import { LogsPanel } from './logs-panel';
import { LinkTicketSection } from './link-ticket-section';

const EDITABLE_STATUSES = ['opening', 'work-in-progress', 'escalated'];

/** Shared loading skeleton for the whole page while ticket detail loads. */
function PageSkeleton() {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Skeleton style={{ height: 32, width: 200 }} />
      <Skeleton style={{ height: 80 }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 'var(--vn-space-4)' }}>
        <Skeleton style={{ height: 400 }} />
        <Skeleton style={{ height: 400 }} />
      </div>
    </div>
  );
}

/**
 * Ticket detail page — `/tickets/:id`
 *
 * Layout: header (id, type, status, priority, customer/service links, assignees)
 *   + two-column body: main (tabs) left + info sidebar + logs panel right.
 *   On narrow viewports the sidebar stacks above content and logs become a tab.
 *
 * Export: `TicketDetailPage` (default export).
 * Route param: `id` (string → number).
 * Lead must add route: `<Route path="/tickets/:id" element={<TicketDetailPage />} />`
 */
export default function TicketDetailPage() {
  const { id: idParam } = useParams();
  const id = Number(idParam) || 0;

  const { data, isLoading, isError, refetch } = useTicket(id);
  const ticket = data?.result;

  const status = ticket?.ticket_status ?? '';

  // Feedback tab is only shown for error_service tickets that are completed/closed.
  // Legacy gate: ticket_type_key === 'error_service' (not 'error_service_incident').
  const showFeedbackTab =
    ticket?.ticket_type_key === 'error_service' &&
    [TICKET_STATUS_COMPLETED, TICKET_STATUS_CLOSED].includes(status);

  const canEdit = EDITABLE_STATUSES.includes(status);
  const isMajorIncident = ticket?.ticket_marjorable?.[0] === true;
  const hasNoAssignees =
    Array.isArray(ticket?.ticket_technical_assignees) &&
    ticket.ticket_technical_assignees.length === 0;

  if (isLoading) return <Container size="full"><PageSkeleton /></Container>;

  if (isError || !ticket) {
    return (
      <Container size="full">
        <Alert
          tone="danger"
          title="Failed to load ticket"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>Retry</Button>
          }
        />
      </Container>
    );
  }

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>

        <DetailHeader
          parent={{ label: 'Tickets', to: '/tickets' }}
          current={ticket.ticket_type_title || `Ticket ${ticket.ticket_id}`}
          idPrefix={`#${ticket.ticket_id}`}
          title={ticket.ticket_type_title || `Ticket ${ticket.ticket_id}`}
          meta={
            <>
              <StatusPill tone={ticketStatusTone(status)}>{ticketStatusLabel(status)}</StatusPill>
              {ticket.ticket_priority && (
                <StatusPill tone={ticketPriorityTone(ticket.ticket_priority)}>
                  {ticket.ticket_priority.toUpperCase()}
                </StatusPill>
              )}
              {ticket.ticket_security_tag && ticket.ticket_security_tag !== 'none' && (
                <Badge>{ticket.ticket_security_tag.toUpperCase()}</Badge>
              )}
            </>
          }
          actions={
            <Button size="sm" variant="secondary" disabled={!canEdit} asChild={canEdit}>
              {canEdit ? (
                <Link to={`/tickets/${ticket.ticket_id}/edit`}>
                  <Pencil size={14} style={{ marginRight: 4 }} />
                  Edit
                </Link>
              ) : (
                <span>
                  <Pencil size={14} style={{ marginRight: 4 }} />
                  Edit
                </span>
              )}
            </Button>
          }
        />

        {/* Alerts (customer/service/assignee facts live in the sidebar, not duplicated here) */}
        {(isMajorIncident || hasNoAssignees) && (
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {/* Major incident banner */}
            {isMajorIncident && <Alert tone="danger" title="Major Incident" />}

            {/* Unassigned warning */}
            {hasNoAssignees && (
              <Alert tone="info" title="No technical assignee">
                This ticket has not been assigned to a technical team member yet.
              </Alert>
            )}
          </div>
        )}

        {/* Body: conversation (primary) + right properties rail.
            Note: responsive two-column breakpoints (md+ → 1fr 320px rail)
            require a CSS module or global stylesheet, not inline styles.
            The className below is retained for future CSS module migration. */}
        <div
          style={{
            display: 'grid',
            gap: 'var(--vn-space-4)',
            gridTemplateColumns: 'minmax(0, 1fr) 320px',
            alignItems: 'start',
          }}
          className="ticket-detail-body"
        >

          {/* Primary column: the conversation IS the page. Description is pinned as
              the first message; the rest stack as sections below it (few enough that
              tabs would just add clicks). */}
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Card flat>
              <CardBody>
                <ConversationTab
                  ticketId={ticket.ticket_id}
                  originalRequest={
                    ticket.ticket_content
                      ? {
                          content: ticket.ticket_content,
                          author: ticket.employee_name || ticket.created_by_name,
                          at: ticket.ticket_requested_at || ticket.created_at,
                        }
                      : undefined
                  }
                />
              </CardBody>
            </Card>

            <Card flat>
              <CardBody>
                <WorkflowTab ticket={ticket} />
              </CardBody>
            </Card>

            <Card flat>
              <CardBody>
                <AttachmentsTab ticketId={ticket.ticket_id} />
              </CardBody>
            </Card>

            <Card flat>
              <CardBody>
                <LinkTicketSection ticket={ticket} />
              </CardBody>
            </Card>

            {showFeedbackTab && (
              <Card flat>
                <CardBody>
                  <FeedbackTab ticketId={ticket.ticket_id} />
                </CardBody>
              </Card>
            )}

            {/* Lifecycle milestones (events) — target deadline lives in the SLA card */}
            <Card flat>
              <CardBody>
                <InfoSection title="Lifecycle">
                  <Timeline size="sm" items={ticketTimeline(ticket)} />
                </InfoSection>
              </CardBody>
            </Card>

            {/* System audit history — separate from the human conversation */}
            <Card flat style={{ overflow: 'hidden' }}>
              <LogsPanel ticketId={ticket.ticket_id} />
            </Card>
          </div>

          {/* Right properties rail */}
          <div
            style={{
              display: 'grid',
              gap: 'var(--vn-space-4)',
              alignContent: 'start',
            }}
          >
            {/* Info card */}
            <Card flat>
              <CardBody>
              <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>

                {/* SLA & status — first-class, above the fold (target time, not an event) */}
                <InfoSection title="SLA & status">
                  <InfoGrid
                    minColWidth={240}
                    items={[
                      {
                        label: 'First response',
                        value: (
                          <StatusPill tone={ticket.ticket_response_late ? 'danger' : 'success'}>
                            {ticket.ticket_response_late ? 'Late' : 'On time'}
                          </StatusPill>
                        ),
                      },
                      { label: 'Deadline', value: fmtDate(ticket.ticket_deadline_at) },
                      { label: 'Est. completion', value: fmtDate(ticket.ticket_estimated_completion_date) },
                    ]}
                  />
                </InfoSection>

                {/* People section */}
                <InfoSection title="People">
                  <InfoGrid
                    minColWidth={240}
                    items={[
                      { label: 'Reporter', value: ticket.employee_name },
                      {
                        label: 'Technical assignees',
                        value: ticket.ticket_technical_assignee_names?.length
                          ? ticket.ticket_technical_assignee_names.map((a) => a.employee_name).join(', ')
                          : undefined,
                      },
                    ]}
                  />
                </InfoSection>

                {/* Customer & service section */}
                <InfoSection title="Customer & Service">
                  <InfoGrid
                    minColWidth={240}
                    items={[
                      {
                        label: 'Customer',
                        value: ticket.customer_id ? (
                          <TableLink to={`/customers/${ticket.customer_id}`} truncate>
                            {ticket.company_name || ticket.customer_name || `#${ticket.customer_id}`}
                          </TableLink>
                        ) : (
                          ticket.customer_name || undefined
                        ),
                      },
                      ...(ticket.service_id && ticket.service_id > 0
                        ? [
                            {
                              label: 'Service',
                              value: (
                                <TableLink to={`/services/${ticket.service_id}`} truncate>
                                  {(Array.isArray(ticket.product_group_name)
                                    ? ticket.product_group_name.join(', ')
                                    : ticket.product_group_name) || ticket.service_name}{' '}
                                  (#{ticket.service_id})
                                </TableLink>
                              ),
                            } satisfies InfoItem,
                          ]
                        : []),
                    ]}
                  />
                </InfoSection>

                {/* Source / product / group meta */}
                {(ticket.ticket_source || ticket.ticket_product_name?.length) && (
                  <InfoSection title="Details">
                    <InfoGrid
                      minColWidth={240}
                      items={[
                        ...(ticket.ticket_source
                          ? [{ label: 'Source', value: ticket.ticket_source }]
                          : []),
                        ...(ticket.ticket_product_name?.length
                          ? [
                              {
                                label: 'Product',
                                value: Array.isArray(ticket.ticket_product_name)
                                  ? ticket.ticket_product_name.join(', ')
                                  : ticket.ticket_product_name,
                              },
                            ]
                          : []),
                      ]}
                    />
                  </InfoSection>
                )}
              </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </Container>
  );
}

/* ── Local helpers ───────────────────────────────────────────────────────── */

function fmtDate(value?: string | null): string | undefined {
  if (!value) return undefined;
  const formatted = formatDateTime(value);
  return formatted === '—' ? undefined : formatted;
}

/**
 * Build the ticket lifecycle as a vertical @ui Timeline. Reached milestones show
 * a solid tone + their timestamp; not-yet-reached ones render hollow (`muted`)
 * with a "Pending" placeholder — so the sequence reads at a glance instead of a
 * dense label/value grid where datetimes wrapped.
 */
function ticketTimeline(ticket: Ticket): TimelineItem[] {
  const requested = fmtDate(ticket.ticket_requested_at);
  const firstReply = fmtDate(ticket.ticket_time_repply);
  const response = fmtDate(ticket.ticket_time_response);
  const completed = fmtDate(ticket.ticket_time_release || ticket.ticket_resolved_at);

  const milestone = (
    title: string,
    at: string | undefined,
    doneTone: TimelineItem['tone'] = 'success',
  ): TimelineItem => ({
    title,
    timestamp: at ?? 'Pending',
    tone: at ? doneTone : 'neutral',
    muted: !at,
  });

  // Events only — the estimated-completion target lives in the SLA card, not here.
  const items: TimelineItem[] = [milestone('Requested', requested), milestone('First reply', firstReply)];
  if (response) items.push(milestone('Response', response));
  items.push(milestone('Completed', completed, 'primary'));
  return items;
}

/** Sidebar info group with an uppercase section heading. Uses Section from @ui/layout. */
function InfoSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Section title={title}>
      <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
        {children}
      </div>
    </Section>
  );
}
